import React, { useEffect, useState } from "react";

const R = 26;
const START = [70, 180 - R];
const END = [446, 132];
const FLIGHT = `M${START[0]} ${START[1]} Q 250 6 ${END[0]} ${END[1]}`;
const DUR = "6s";
const TIMES = "0;0.15;0.45;0.88;0.9;1";
const SPLINES = "0 0 1 1;0.25 0.1 0.55 1;0 0 1 1;0 0 1 1;0 0 1 1";

function useReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );
  useEffect(() => {
    const query = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!query) return undefined;
    const onChange = () => setReduced(query.matches);
    query.addEventListener?.("change", onChange);
    return () => query.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

const at = (radius, deg) => {
  const a = (deg * Math.PI) / 180;
  return [radius * Math.cos(a), radius * Math.sin(a)];
};
const pts = (list) => list.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(" ");

const ANGLES = [0, 1, 2, 3, 4].map((k) => -90 + 72 * k);
const CENTER = ANGLES.map((a) => at(R * 0.34, a));
const OUTER = ANGLES.map((a) => {
  const [cx, cy] = at(R * 0.96, a);
  return [0, 1, 2, 3, 4].map((j) => {
    const [dx, dy] = at(R * 0.3, a + 180 + 72 * j);
    return [cx + dx, cy + dy];
  });
});

function Ball() {
  return (
    <g>
      <defs>
        <clipPath id="p90-ball-clip">
          <circle r={R} />
        </clipPath>
        <radialGradient id="p90-ball-shade" cx="35%" cy="30%" r="75%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#e2e8f0" />
        </radialGradient>
      </defs>
      <circle r={R} fill="url(#p90-ball-shade)" />
      <g clipPath="url(#p90-ball-clip)" stroke="#1f2937" strokeWidth="1.4" strokeLinejoin="round">
        <polygon points={pts(CENTER)} fill="#111827" />
        {OUTER.map((poly, k) => (
          <polygon key={k} points={pts(poly)} fill="#111827" />
        ))}
        {ANGLES.map((a, k) => (
          <line key={`s${k}`} x1={CENTER[k][0]} y1={CENTER[k][1]} x2={OUTER[k][0][0]} y2={OUTER[k][0][1]} />
        ))}
        {OUTER.map((poly, k) => {
          const next = OUTER[(k + 1) % 5];
          return <line key={`h${k}`} x1={poly[4][0]} y1={poly[4][1]} x2={next[1][0]} y2={next[1][1]} />;
        })}
      </g>
      <circle r={R} fill="none" stroke="#111827" strokeWidth="1.6" />
    </g>
  );
}

function Goal({ animate }) {
  const net = [];
  for (let x = 398; x <= 482; x += 12) net.push(<line key={`v${x}`} x1={x} y1="74" x2={x} y2="180" />);
  for (let y = 84; y <= 176; y += 12) net.push(<line key={`h${y}`} x1="396" y1={y} x2="484" y2={y} />);

  return (
    <g>
      <g stroke="#cbd5e1" strokeWidth="1">
        {net}
        <polyline points="384,62 396,74 484,74 496,62" fill="none" />
        <line x1="396" y1="74" x2="396" y2="180" />
        <line x1="484" y1="74" x2="484" y2="180" />
        {animate && (
          <animateTransform
            attributeName="transform"
            type="translate"
            values="0 0;0 0;0 0;5 -2;0 0;0 0"
            keyTimes="0;0.15;0.45;0.49;0.6;1"
            dur={DUR}
            repeatCount="indefinite"
          />
        )}
      </g>
    </g>
  );
}

function Frame() {
  return (
    <path d="M384 180 V62 H496 V180" fill="none" stroke="#334155" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
  );
}

export default function GoalShot({ className = "" }) {
  const reduced = useReducedMotion();

  return (
    <svg viewBox="0 0 520 190" className={`w-full ${className}`} aria-hidden="true" focusable="false">
      <line x1="0" y1="181" x2="520" y2="181" stroke="#e5e7eb" strokeWidth="2" />
      <Goal animate={!reduced} />

      <ellipse cx={reduced ? END[0] : START[0]} cy="182" rx="20" ry="3.5" fill="#111827" opacity="0.12">
        {!reduced && (
          <animate
            attributeName="cx"
            values={`${START[0]};${START[0]};${END[0]};${END[0]};${START[0]};${START[0]}`}
            keyTimes={TIMES}
            calcMode="spline"
            keySplines={SPLINES}
            dur={DUR}
            repeatCount="indefinite"
          />
        )}
      </ellipse>

      {reduced ? (
        <g transform={`translate(${END[0]} ${END[1]}) scale(0.8)`}>
          <Ball />
        </g>
      ) : (
        <g>
          <animateMotion
            path={FLIGHT}
            keyPoints="0;0;1;1;0;0"
            keyTimes={TIMES}
            calcMode="spline"
            keySplines={SPLINES}
            dur={DUR}
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="1;1;0;0;1"
            keyTimes="0;0.85;0.87;0.93;1"
            dur={DUR}
            repeatCount="indefinite"
          />
          <g>
            <animateTransform
              attributeName="transform"
              type="scale"
              values="1;1;0.8;0.8;1;1"
              keyTimes={TIMES}
              dur={DUR}
              repeatCount="indefinite"
            />
            <g>
              <animateTransform
                attributeName="transform"
                type="rotate"
                values="0;0;300;300;0;0"
                keyTimes={TIMES}
                dur={DUR}
                repeatCount="indefinite"
              />
              <Ball />
            </g>
          </g>
        </g>
      )}
      <Frame />
    </svg>
  );
}
